import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPkr } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { listPayouts, markPayoutPaid } from '../api/admin.js';
import { useToast } from '../components/Toast.js';

type PayoutRow = Awaited<ReturnType<typeof listPayouts>>[number];

export function PayoutsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const payouts = useQuery({ queryKey: ['admin-payouts'], queryFn: listPayouts });
  const markPaid = useMutation({
    mutationFn: markPayoutPaid,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payouts'] });
      toast.show('Payout marked as paid.', 'success');
    },
    onError: () => toast.show('Could not update the payout.', 'error'),
  });

  const columns: Column<PayoutRow>[] = [
    { key: 'driver', header: 'Driver', cell: (p) => <span className="font-medium">{p.driver.user.name}</span> },
    {
      key: 'period',
      header: 'Period',
      cell: (p) =>
        `${new Date(p.periodStart).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' })} – ${new Date(p.periodEnd).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' })}`,
    },
    { key: 'amount', header: 'Amount', align: 'right', cell: (p) => <span className="num font-semibold">{formatPkr(p.amount)}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (p) =>
        p.status === 'paid' ? (
          <span className="pill bg-emerald-100 text-emerald-800">Paid</span>
        ) : (
          <span className="pill bg-amber-100 text-amber-800">Pending</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (p) =>
        p.status === 'paid' ? null : (
          <button
            type="button"
            className="btn-primary"
            onClick={() => markPaid.mutate(p.id)}
            disabled={markPaid.isPending}
          >
            Mark paid
          </button>
        ),
    },
  ];

  return (
    <Shell title="Payouts" subtitle="Driver earnings ready for transfer.">
      <DataTable
        rows={payouts.data ?? []}
        columns={columns}
        rowKey={(p) => p.id}
        loading={payouts.isLoading}
        emptyMessage="No payouts have been scheduled yet."
      />
    </Shell>
  );
}
